/**
 * Typed dataLayer helpers for Google Tag Manager.
 *
 * Every conversion-relevant interaction on stylon.co flows through this module — call
 * one of the exported functions, and it pushes a normalized event to window.dataLayer.
 * GTM (configured separately) listens for these events and fires the appropriate Google
 * Ads conversion tags + GA4 events.
 *
 * Why this module exists:
 *   - Single, typed surface for all tracking — no copy-pasted gtag/dataLayer calls.
 *   - Centralized error handling (tracking failures must never break the user UX).
 *   - Easy to add new platforms (Meta Pixel, TikTok) — only this file changes.
 *   - Enables stripping in tests / dev with an env toggle.
 *
 * Event naming follows Google's recommended GA4 event names where they exist
 * (page_view, sign_up, begin_checkout, purchase) — this gives auto-recognition
 * by Google's algorithms without further configuration.
 */
import { hashEmail, hashPhone } from './hash';
import { mapFunnelEventToMeta, pixelTrack, pixelTrackCustom } from './metaPixel';

// ── Lifecycle helpers ────────────────────────────────────────────────────────

function getDataLayer() {
  if (typeof window === 'undefined') return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

/**
 * Internal: push a structured event to GTM. All other helpers go through this so we
 * have one place that decides "to push or not" (e.g. dev mode, opt-out, etc.).
 */
function push(payload) {
  const dl = getDataLayer();
  if (!dl) return;
  try {
    dl.push(payload);
  } catch (_) {
    // Tracking must never throw. Swallow silently.
  }
  // Mirror the funnel event to the platform Meta Pixel (no-op if unset/unmapped —
  // see metaPixel.js). Kept as a second, independent call so a pixel failure can
  // never affect the GTM/dataLayer push above.
  try {
    const meta = mapFunnelEventToMeta(payload && payload.event);
    // Only purchase/lead carry monetary params to the pixel (matches Meta's
    // Purchase/Lead standard-event schema); other mapped events fire bare.
    let params;
    if (payload && payload.event === 'purchase') {
      params = { value: payload.value, currency: payload.currency };
    } else if (payload && payload.event === 'lead' && payload.value !== undefined) {
      params = { value: payload.value, currency: payload.currency };
    }
    if (meta.type === 'track') {
      pixelTrack(meta.name, params);
    } else if (meta.type === 'trackCustom') {
      pixelTrackCustom(meta.name, params);
    }
  } catch (_) {
    // Tracking must never throw. Swallow silently.
  }
}

/** GCLID capture/restore across page loads (the click ID Google Ads uses for attribution). */
const GCLID_STORAGE_KEY = 'stylon_gclid';
const GCLID_TS_STORAGE_KEY = 'stylon_gclid_ts';
const GCLID_TTL_DAYS = 90; // matches Google Ads default attribution window

export function captureGclidFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const gclid = params.get('gclid') || params.get('gbraid') || params.get('wbraid');
    if (gclid) {
      localStorage.setItem(GCLID_STORAGE_KEY, gclid);
      localStorage.setItem(GCLID_TS_STORAGE_KEY, String(Date.now()));
    }
  } catch (_) {}
}

export function getStoredGclid() {
  try {
    const ts = parseInt(localStorage.getItem(GCLID_TS_STORAGE_KEY) || '0', 10);
    if (!ts) return '';
    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    if (ageDays > GCLID_TTL_DAYS) {
      localStorage.removeItem(GCLID_STORAGE_KEY);
      localStorage.removeItem(GCLID_TS_STORAGE_KEY);
      return '';
    }
    return localStorage.getItem(GCLID_STORAGE_KEY) || '';
  } catch (_) {
    return '';
  }
}

/** FBCLID capture/restore across page loads (the click ID Meta/Facebook uses for attribution). */
const FBCLID_STORAGE_KEY = 'stylon_fbclid';
const FBCLID_TS_STORAGE_KEY = 'stylon_fbclid_ts';
const FBCLID_TTL_DAYS = 90;

export function captureFbclidFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get('fbclid');
    if (fbclid) {
      localStorage.setItem(FBCLID_STORAGE_KEY, fbclid);
      localStorage.setItem(FBCLID_TS_STORAGE_KEY, String(Date.now()));
    }
  } catch (_) {}
}

export function getStoredFbclid() {
  try {
    const ts = parseInt(localStorage.getItem(FBCLID_TS_STORAGE_KEY) || '0', 10);
    if (!ts) return '';
    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    if (ageDays > FBCLID_TTL_DAYS) {
      localStorage.removeItem(FBCLID_STORAGE_KEY);
      localStorage.removeItem(FBCLID_TS_STORAGE_KEY);
      return '';
    }
    return localStorage.getItem(FBCLID_STORAGE_KEY) || '';
  } catch (_) {
    return '';
  }
}

// ── Public event helpers ─────────────────────────────────────────────────────

/**
 * page_view — fired on every route change (and on initial load).
 * Google's standard pageview event; auto-recognized by GA4.
 */
export function trackPageView({ path, title, lang } = {}) {
  push({
    event: 'page_view',
    page_path: path || (typeof window !== 'undefined' ? window.location.pathname : ''),
    page_title: title || (typeof document !== 'undefined' ? document.title : ''),
    page_lang: lang || (typeof document !== 'undefined' ? document.documentElement.lang : ''),
  });
}

/**
 * start_onboarding_click — every "Start Now" / "Get Started" / "Try Free" CTA on the
 * landing site. Top-of-funnel micro-conversion; useful for retargeting audiences.
 *
 * @param source — where on the site the user clicked (e.g. 'hero', 'navbar',
 *                 'category_hair-salons', 'demo_modal', 'footer').
 */
export function trackStartOnboardingClick(source) {
  push({
    event: 'start_onboarding_click',
    cta_source: source || 'unknown',
    cta_page: typeof window !== 'undefined' ? window.location.pathname : '',
  });
}

/**
 * lead — phone number successfully submitted via the landing CallMe form.
 * Treated as a conversion in Google Ads (Lead category).
 *
 * Phone is hashed before pushing so Google's Enhanced Conversions can match
 * the lead to a future signed-in user without ever seeing the raw number.
 */
export async function trackLead({ phone, source }) {
  const sha256 = phone ? await hashPhone(phone) : '';
  push({
    event: 'lead',
    cta_source: source || 'phone_form',
    cta_page: typeof window !== 'undefined' ? window.location.pathname : '',
    // GA4 standard "generate_lead" parameters
    value: 10,
    currency: 'ILS',
    // Enhanced Conversions user_data (Google reads this from dataLayer if the GTM
    // user-data variable is configured to do so; we do that in the container).
    user_data: { sha256_phone_number: sha256 },
  });
}

/** view_pricing — when user lands on /pricing. Engagement signal. */
export function trackViewPricing() {
  push({ event: 'view_pricing' });
}

/** view_features — when user lands on /features or any feature subpage. */
export function trackViewFeatures({ feature } = {}) {
  push({ event: 'view_features', feature_key: feature || '' });
}

/** view_business_category — when user lands on a /for/* category page. */
export function trackViewBusinessCategory({ category } = {}) {
  push({ event: 'view_business_category', category_key: category || '' });
}

// ── Portal / onboarding events (used by the portal app, exported for consistency) ─

/**
 * begin_onboarding — fires when the portal's onboarding wizard first mounts.
 * Captures the GCLID coming over from the landing site so all downstream
 * events (sign_up, purchase) carry it.
 */
export function trackBeginOnboarding({ source } = {}) {
  push({
    event: 'begin_onboarding',
    cta_source: source || '',
    gclid: getStoredGclid(),
  });
}

/** onboarding_step_complete — fires after each step the user finishes. */
export function trackOnboardingStepComplete({ stepIndex, stepName } = {}) {
  push({
    event: 'onboarding_step_complete',
    step_index: stepIndex,
    step_name: stepName || '',
  });
}

/**
 * sign_up — fires when the user has successfully created their business account.
 * This is the Sign-up conversion in Google Ads.
 */
export async function trackSignUp({ userId, businessType, phone, email } = {}) {
  const [sha256_phone, sha256_email] = await Promise.all([
    phone ? hashPhone(phone) : Promise.resolve(''),
    email ? hashEmail(email) : Promise.resolve(''),
  ]);
  push({
    event: 'sign_up',
    method: 'portal',
    user_id: userId || '',
    business_type: businessType || '',
    value: 30,
    currency: 'ILS',
    user_data: {
      sha256_phone_number: sha256_phone,
      sha256_email: sha256_email,
    },
  });
}

/** view_subscription_plans — pricing/upgrade view inside the portal. */
export function trackViewSubscriptionPlans({ plansShown } = {}) {
  push({
    event: 'view_subscription_plans',
    plans_shown: plansShown || [],
  });
}

/** begin_checkout — clicked Subscribe / opened the Tranzila iframe. */
export function trackBeginCheckout({ planId, value, currency, interval } = {}) {
  push({
    event: 'begin_checkout',
    plan_id: planId || '',
    value: value || 0,
    currency: currency || 'ILS',
    interval: interval || 'monthly',
  });
}

/**
 * purchase — Tranzila confirmed the charge.
 * THE main conversion: Google Ads' bidding algorithm optimizes toward this.
 *
 * transaction_id is the Tranzila confirmation code; reused for deduplication if the
 * backend later sends a server-side conversion via the Google Ads API.
 */
export async function trackPurchase({
  transactionId,
  value,
  currency = 'ILS',
  planId,
  phone,
  email,
} = {}) {
  const [sha256_phone, sha256_email] = await Promise.all([
    phone ? hashPhone(phone) : Promise.resolve(''),
    email ? hashEmail(email) : Promise.resolve(''),
  ]);
  push({
    event: 'purchase',
    transaction_id: transactionId || '',
    value: value || 0,
    currency,
    items: [
      {
        item_id: planId || '',
        item_name: planId || '',
        price: value || 0,
        quantity: 1,
      },
    ],
    user_data: {
      sha256_phone_number: sha256_phone,
      sha256_email: sha256_email,
    },
  });
}
